import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  DialogContent,
  TextField,
  Stack,
  Alert,
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { FormDialog } from '@/components/common/FormDialog';
import { PartyPicker, type PartyRef } from '@/components/parties/PartyPicker';
import { useCreateVehicle, useUpdateVehicle, type VehicleListItem, type VehicleDetail } from '@/hooks/useVehicles';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';
import { VehicleFormFields, validateVehicleFields, type VehicleFieldsData } from './VehicleFormFields';

interface VehicleFormDialogProps {
  open: boolean;
  vehicle?: VehicleListItem | null;
  onClose: () => void;
  onCreated?: (vehicle: VehicleDetail) => void;
}

const initialFormData: VehicleFieldsData = {
  registrationNumber: '',
  make: '',
  model: '',
  year: '',
  vin: '',
  mileage: '',
};

export function VehicleFormDialog({ open, vehicle, onClose, onCreated }: VehicleFormDialogProps) {
  const { t } = useTranslation('vehicles');
  const { t: tc } = useTranslation('common');
  const { t: tw } = useTranslation('workorders');
  const notify = useNotify();
  const isEditing = !!vehicle;

  const [formData, setFormData] = useState<VehicleFieldsData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof VehicleFieldsData, string>>>({});
  const [ownerError, setOwnerError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // The owner is a person/company party from the registries (#833 B2).
  const [ownerParty, setOwnerParty] = useState<PartyRef | null>(null);

  const createMutation = useCreateVehicle();
  const updateMutation = useUpdateVehicle();

  useEffect(() => {
    if (vehicle) {
      setFormData({
        registrationNumber: vehicle.registrationNumber,
        make: vehicle.make ?? '',
        model: vehicle.model ?? '',
        year: vehicle.year?.toString() ?? '',
        vin: vehicle.vin ?? '',
        mileage: vehicle.mileage?.toString() ?? '',
      });
    } else {
      setFormData(initialFormData);
    }
    setOwnerParty(null);
    setErrors({});
    setOwnerError(false);
    setIsDirty(false);
  }, [vehicle, open]);

  const validate = (): boolean => {
    const newErrors = validateVehicleFields(formData, t);
    setErrors(newErrors);
    const missingOwner = !isEditing && !ownerParty;
    setOwnerError(missingOwner);
    return Object.keys(newErrors).length === 0 && !missingOwner;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const reg = formData.registrationNumber.trim();

    try {
      if (vehicle) {
        await updateMutation.mutateAsync({
          id: vehicle.id,
          data: {
            registrationNumber: reg,
            make: formData.make.trim() || undefined,
            model: formData.model.trim() || undefined,
            year: formData.year ? Number(formData.year) : undefined,
            vin: formData.vin.trim() || undefined,
            mileage: formData.mileage ? Number(formData.mileage) : undefined,
          },
        });
        notify.success(t('form.updated'), t('form.updatedDesc', { reg }));
      } else {
        const created = await createMutation.mutateAsync({
          owner: ownerParty
            ? {
                partyType: ownerParty.partyType,
                personId: ownerParty.partyType === 'person' ? ownerParty.personId : undefined,
                companyId: ownerParty.partyType === 'company' ? ownerParty.companyId : undefined,
              }
            : undefined,
          registrationNumber: reg,
          make: formData.make.trim() || undefined,
          model: formData.model.trim() || undefined,
          year: formData.year ? Number(formData.year) : undefined,
          vin: formData.vin.trim() || undefined,
          mileage: formData.mileage ? Number(formData.mileage) : undefined,
        });
        notify.success(t('form.created'), t('form.createdDesc', { reg }));
        onCreated?.(created);
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        notify.error(tc('error'), t('form.duplicateRegistration', { reg }));
      } else {
        notify.error(tc('error'), describeApiError(err, tc, { fallback: t('form.saveFailed') }));
      }
    }
  };

  const handleChange = (field: keyof VehicleFieldsData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('editVehicle') : t('addVehicle')}
      isDirty={isDirty}
      onSubmit={() => void handleSubmit()}
      isLoading={isPending}
      submitLabel={isEditing ? tc('save') : tc('add')}
      maxWidth="sm"
    >
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {isEditing ? (
            <TextField
              label={t('owner')}
              value={vehicle.customerName}
              fullWidth
              disabled
            />
          ) : ownerParty ? (
            <Alert
              severity="success"
              icon={false}
              action={
                <Button
                  size="small"
                  startIcon={<SwapHorizIcon fontSize="small" />}
                  onClick={() => {
                    setOwnerParty(null);
                    setIsDirty(true);
                  }}
                >
                  {tc('edit')}
                </Button>
              }
            >
              <Typography variant="subtitle2">{t('owner')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={ownerParty.partyType === 'company' ? tw('parties.company') : tw('parties.person')}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Typography variant="body2">
                  {ownerParty.name}
                  {ownerParty.phone ? ` · ${ownerParty.phone}` : ''}
                </Typography>
              </Box>
            </Alert>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('owner')}</Typography>
              <PartyPicker
                onSelect={(party) => {
                  setOwnerParty(party);
                  setOwnerError(false);
                  setIsDirty(true);
                }}
              />
              {ownerError && (
                <Alert severity="error" sx={{ mt: 1 }}>{t('form.customerRequired')}</Alert>
              )}
            </Box>
          )}

          <VehicleFormFields
            data={formData}
            errors={errors}
            onChange={handleChange}
          />
        </Stack>
      </DialogContent>
    </FormDialog>
  );
}
